/**
 * Le due regioni di annuncio della console.
 *
 * Sono **sempre montate e inizialmente vuote**, ed è l'unico modo in cui
 * funzionano: una regione live che compare nel DOM con il testo già dentro non
 * viene annunciata dalla maggior parte delle combinazioni di browser e screen
 * reader, perché la regione va osservata da prima che il contenuto cambi.
 *
 * `polite` per lo stato del servizio e per gli esiti; `assertive` solo per ciò
 * che non può aspettare — una chiamata in arrivo (sezione 7bis).
 */

type Props = {
  polite: string;
  assertive: string;
  /**
   * Quante volte è stato detto qualcosa.
   *
   * Serve a far rileggere **lo stesso** messaggio: una regione live annuncia
   * quando il contenuto cambia, e chi riprova un'azione che fallisce allo
   * stesso modo non sentirebbe niente la seconda volta. Il contatore sta in un
   * nodo separato e nascosto, così il testo annunciato resta quello e basta.
   */
  seq?: number;
};

export function Announcer({ polite, assertive, seq }: Props) {
  /*
    Il contatore va in **tutte e due** le regioni, e non solo in quella
    gentile.

    Stava solo lì perché tutte le schermate tranne una passano da `polite`.
    L'unica che usa `assertive` è la schermata di accesso, ed è anche quella in
    cui il caso si presenta di più: chi sbaglia la password, riprova e sbaglia
    di nuovo riceve lo stesso testo, quindi nel nodo assertivo non cambiava
    niente e il secondo errore restava muto. Il difetto era in un componente
    corretto, tirato fuori da un uso nuovo (AS7).

    Metterlo in entrambe non costa niente a nessuno: un nodo `hidden` non entra
    nell'albero di accessibilità, quindi una regione che porta solo il
    contatore resta vuota per chi ascolta.
  */
  const counter = seq === undefined ? null : <span hidden>{seq}</span>;

  return (
    <div className="visually-hidden">
      <p role="status" aria-live="polite">
        {polite}
        {counter}
      </p>
      <p role="alert" aria-live="assertive">
        {assertive}
        {counter}
      </p>
    </div>
  );
}
